import React, { useEffect, useState } from 'react';

import useAsync from '../hooks/useAsync';
import useModal from '../hooks/useModal';

import { StackableModal } from '../components/nextgen';
import { addLocation, getLocations, editLocation } from '../api';
import SelectLocations from '../components/SelectLocation';

const MODAL_NAME = 'addLocation';
const defaultForm = { type: 'group' };

const AddLocation = () => {
	const { open, setClose } = useModal(MODAL_NAME);

	const isEditing = !!open?.id;

	const [form, setForm] = useState(defaultForm);
	const resetForm = (form = {}) => setForm({ ...defaultForm, ...form });

	const { value: locations, execute: refreshLocations } = useAsync(
		getLocations,
		{ immediate: false },
	);

	const {
		execute: onAdd,
		status,
		error,
		resetError,
	} = useAsync(addLocation, {
		immediate: false,
	});

	const { execute: onEdit } = useAsync(editLocation, {
		immediate: false,
	});

	const onAction = isEditing ? onEdit : onAdd;

	useEffect(() => {
		if (open) {
			refreshLocations();
			resetForm(open?.id ? open : {});
		} else {
			resetError();
			resetForm();
		}
	}, [open]);

	const hasError = status === 'error';
	const isLoading = status === 'pending';

	const handleClose = () => {
		resetForm();
		setClose();
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		onAction(configurePayload(form), (response) => {
			if (typeof open?.onSubmit === 'function') {
				open?.onSubmit({
					label: response.name,
					value: response.id,
					id: response.id,
					name: response.name,
				});
				resetForm();
			}
			setClose();
		});
	};

	const title = isEditing ? `Edit Location: ${open?.name}` : 'Add Location';

	return (
		<StackableModal
			open={!!open}
			onClose={handleClose}
			title={title}
			size="md"
		>
			<form onSubmit={handleSubmit}>
				{hasError && (
					<div
						className="p-4 mb-4 text-xs text-red-700 bg-red-100 rounded-lg"
						role="alert"
					>
						<span className="font-medium">Uh oh, </span>something went wrong
					</div>
				)}

				<div className="space-y-4">
					{/* Location Name */}
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Location Name <span className="text-red-500">*</span>
						</label>
						<input
							type="text"
							placeholder="Enter location name"
							value={form.name || ''}
							onChange={(e) => setForm({ ...form, name: e.target.value })}
							className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
						/>
					</div>

					{/* Parent Location */}
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Parent Location{' '}
							<span className="text-gray-400 font-normal">(Optional)</span>
						</label>
						<SelectLocations
							locations={locations}
							onChange={(e) => setForm({ ...form, parent_id: e })}
							value={form.parent_id}
							skip={['bench']}
							preventNew
							placeholder="Select a parent location"
						/>
					</div>
				</div>

				{/* Footer Actions */}
				<div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
					<button
						type="button"
						onClick={handleClose}
						className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
					>
						Cancel
					</button>
					<button
						type="submit"
						disabled={!form.name?.trim() || isLoading}
						className={`px-4 py-2 text-sm font-medium rounded-lg ${
							!form.name?.trim() || isLoading
								? 'bg-gray-100 text-gray-400 cursor-not-allowed'
								: 'bg-indigo-600 text-white hover:bg-indigo-700'
						}`}
					>
						{isLoading
							? isEditing
								? 'Updating...'
								: 'Adding...'
							: isEditing
								? 'Update Location'
								: 'Add Location'}
					</button>
				</div>
			</form>
		</StackableModal>
	);
};

const configurePayload = ({ id, name, parent_id, type }) => ({
	id,
	name,
	parentId: parent_id || 'undefined',
	type,
});

export default AddLocation;
